import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useAuthStore from './stores/authStore';

// Layout
import { AppLayout } from './components/layout';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Main Pages
import Dashboard from './pages/Dashboard';
import { UserList } from './pages/users';
import { VendorList, VendorRegistration } from './pages/vendors';
import { PurchaseRequestList, PurchaseRequestForm, PurchaseRequestDetail } from './pages/purchase-requests';
import { CatalogList, UOMList } from './pages/catalog';
import { BudgetList, FiscalYearList, BudgetTransferList } from './pages/budgets';
import { DepartmentList } from './pages/departments';
import { RoleList } from './pages/roles';
import { RFQList, RFQForm, RFQDetail, RFQAward, QuotationForm } from './pages/rfq';
import { ContractList, ContractForm, ContractDetail } from './pages/contracts';
import { PurchaseOrderList, PurchaseOrderForm, PurchaseOrderDetail } from './pages/purchase-orders';
import { GoodsReceiptList, GoodsReceiptDetail } from './pages/goods-receipts';
import { InvoiceList, PaymentList } from './pages/invoices';
import { ReportsDashboard, POReport, VendorPerformanceReport, InvoiceAgingReport, ContractExpiryReport, SavingsReport, CycleTimeReport } from './pages/reports';
import { NotificationList } from './pages/notifications';
import { SettingsLayout, CompanySettings } from './pages/settings';
import { VendorDashboard, VendorProfile } from './pages/vendor-portal';
import { AuditLogList } from './pages/audit-log';
import { ComplianceDashboard } from './pages/compliance';
import { UserProfile } from './pages/profile';

// Create React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

// Protected Route wrapper
function ProtectedRoute({ children }) {
    const { isAuthenticated } = useAuthStore();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

// Public Route wrapper (redirect if authenticated)
function PublicRoute({ children }) {
    const { isAuthenticated } = useAuthStore();

    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={
                        <PublicRoute><Login /></PublicRoute>
                    } />
                    <Route path="/forgot-password" element={
                        <PublicRoute><ForgotPassword /></PublicRoute>
                    } />
                    <Route path="/reset-password" element={
                        <PublicRoute><ResetPassword /></PublicRoute>
                    } />
                    <Route path="/vendor-registration" element={<VendorRegistration />} />

                    {/* Protected Routes */}
                    <Route element={
                        <ProtectedRoute><AppLayout /></ProtectedRoute>
                    }>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/profile" element={<UserProfile />} />

                        {/* Purchase Requests */}
                        <Route path="/purchase-requests" element={<PurchaseRequestList />} />
                        <Route path="/purchase-requests/create" element={<PurchaseRequestForm />} />
                        <Route path="/purchase-requests/:id" element={<PurchaseRequestDetail />} />
                        <Route path="/purchase-requests/:id/edit" element={<PurchaseRequestForm />} />

                        {/* RFQ */}
                        <Route path="/rfq" element={<RFQList />} />
                        <Route path="/rfq/create" element={<RFQForm />} />
                        <Route path="/rfq/:id" element={<RFQDetail />} />
                        <Route path="/rfq/:id/edit" element={<RFQForm />} />
                        <Route path="/rfq/:id/award" element={<RFQAward />} />
                        <Route path="/rfq/:rfqId/quotation" element={<QuotationForm />} />
                        <Route path="/rfq/:rfqId/quotation/:quotationId" element={<QuotationForm />} />

                        {/* Contracts */}
                        <Route path="/contracts" element={<ContractList />} />
                        <Route path="/contracts/create" element={<ContractForm />} />
                        <Route path="/contracts/:id" element={<ContractDetail />} />
                        <Route path="/contracts/:id/edit" element={<ContractForm />} />

                        {/* Purchase Orders */}
                        <Route path="/purchase-orders" element={<PurchaseOrderList />} />
                        <Route path="/purchase-orders/create" element={<PurchaseOrderForm />} />
                        <Route path="/purchase-orders/:id" element={<PurchaseOrderDetail />} />
                        <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderForm />} />

                        {/* Goods Receipt */}
                        <Route path="/goods-receipts" element={<GoodsReceiptList />} />
                        <Route path="/goods-receipts/:id" element={<GoodsReceiptDetail />} />

                        {/* Invoices & Payments */}
                        <Route path="/invoices" element={<InvoiceList />} />
                        <Route path="/payments" element={<PaymentList />} />

                        {/* Vendors */}
                        <Route path="/vendors" element={<VendorList />} />

                        {/* Catalog */}
                        <Route path="/catalog" element={<CatalogList />} />
                        <Route path="/catalog/uom" element={<UOMList />} />

                        {/* Budgets */}
                        <Route path="/budgets" element={<BudgetList />} />
                        <Route path="/budgets/fiscal-years" element={<FiscalYearList />} />
                        <Route path="/budgets/transfers" element={<BudgetTransferList />} />

                        {/* Reports */}
                        <Route path="/reports" element={<ReportsDashboard />} />
                        <Route path="/reports/purchase-orders" element={<POReport />} />
                        <Route path="/reports/vendor-performance" element={<VendorPerformanceReport />} />
                        <Route path="/reports/invoice-aging" element={<InvoiceAgingReport />} />
                        <Route path="/reports/contract-expiry" element={<ContractExpiryReport />} />
                        <Route path="/reports/savings" element={<SavingsReport />} />
                        <Route path="/reports/cycle-time" element={<CycleTimeReport />} />

                        {/* Notifications */}
                        <Route path="/notifications" element={<NotificationList />} />

                        {/* Users & Administration */}
                        <Route path="/users" element={<UserList />} />
                        <Route path="/departments" element={<DepartmentList />} />
                        <Route path="/roles" element={<RoleList />} />

                        {/* Settings */}
                        <Route path="/settings" element={<SettingsLayout />} />
                        <Route path="/settings/company" element={<SettingsLayout><CompanySettings /></SettingsLayout>} />

                        {/* Vendor Portal */}
                        <Route path="/vendor-portal" element={<VendorDashboard />} />
                        <Route path="/vendor-portal/profile" element={<VendorProfile />} />
                        <Route path="/vendor-portal/rfq/:rfqId/respond" element={<QuotationForm />} />

                        {/* Audit Log */}
                        <Route path="/audit-logs" element={<AuditLogList />} />

                        {/* Compliance */}
                        <Route path="/compliance" element={<ComplianceDashboard />} />
                    </Route>

                    {/* Catch all */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </QueryClientProvider>
    );
}

// Placeholder component for unfinished pages
function ComingSoon({ title }) {
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
            textAlign: 'center'
        }}>
            <h1 style={{ fontSize: '2rem', color: '#111827', marginBottom: '0.5rem' }}>{title}</h1>
            <p style={{ color: '#6b7280' }}>This page is under development. Check back soon!</p>
        </div>
    );
}
